import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool, ToolContext } from '../BaseTool';
import { createWall, processWallJoins } from '../WallTool';
import { WallElement, BIMElement } from '../../core/types';
import { WallJoinSolver } from '../../core/WallJoinSolver';
import { scheduleBackgroundWork } from '../../utils/backgroundScheduler';

export class WallToolClass extends BaseTool {
    id = 'WALL';
    name = 'Wall';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    // Dedupe guard: grid-snapped preview coordinates fully determine the preview,
    // so identical consecutive snapshots skip the store write entirely.
    private lastPreviewKey: string | null = null;

    // Tolerance for treating a snapped reading as "exactly at the origin":
    // a genuine grid-snapped click only lands on (0,0) when the cursor really
    // sits there, while a stale event-layer ray collapses exactly onto it.
    private static readonly ORIGIN_EPSILON = 1e-3;
    // Max distance (meters, one grid cell) between the tool's recomputed ray
    // hit and R3F's own event intersection for the two to count as agreeing.
    private static readonly READING_TOLERANCE = 0.5;
    // Consecutive origin-anchor deferrals; the second is accepted so a user
    // genuinely starting a wall at the origin is never locked out.
    private originDeferrals = 0;

    /**
     * Cross-validate a pointer reading against R3F's own root-layer plane
     * intersection (event.point). The recomputed ray can momentarily collapse
     * onto the exact world origin (stale event-layer ray after tool
     * activation), while R3F's event.point for the root event plane is
     * authoritative. The collapsed reading is replaced by R3F's intersection
     * whenever the two disagree; an uncorroborated origin reading is deferred
     * once and accepted on the second occurrence so a genuine wall start at
     * the origin is only delayed, never dropped.
     *
     * Returns the trusted grid-snapped point, or null to defer this event.
     */
    private resolveTrustedPoint(event: ThreeEvent<PointerEvent>, recomputed: THREE.Vector3): THREE.Vector3 | null {
        const snapped = this.resolvePoint(recomputed);
        const snappedAtOrigin =
            Math.abs(snapped.x) < WallToolClass.ORIGIN_EPSILON &&
            Math.abs(snapped.y) < WallToolClass.ORIGIN_EPSILON;

        if (!snappedAtOrigin) {
            this.originDeferrals = 0;
            return snapped;
        }

        const r3fPoint = event.point;
        const r3fUsable =
            Number.isFinite(r3fPoint?.x) && Number.isFinite(r3fPoint?.y);

        if (r3fUsable) {
            const agrees =
                Math.hypot(r3fPoint.x - recomputed.x, r3fPoint.y - recomputed.y) <=
                WallToolClass.READING_TOLERANCE;
            this.originDeferrals = 0;
            return agrees ? snapped : this.resolvePoint(r3fPoint.clone());
        }

        // No usable cross-reference: defer the first occurrence, accept the
        // second so a genuine start at the origin still works.
        this.originDeferrals++;
        if (this.originDeferrals >= 2) {
            this.originDeferrals = 0;
            return snapped;
        }
        return null;
    }

    /**
     * Resolve a raw ground reading into the drawing point: grid snap, then the
     * shared ortho constraint (F8 toggle or Shift hold) relative to the
     * current segment anchor. With no anchor the ortho step is a no-op.
     */
    private resolvePoint(point: THREE.Vector3): THREE.Vector3 {
        return this.applyDirectionalSnap(this.snapToGrid(point), this.startPoint);
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        if (!this.startPoint) {
            // Start drawing: cross-validate the reading before anchoring, so
            // a ghost origin click can never become the segment's anchor.
            const anchor = this.resolveTrustedPoint(event, point);
            if (!anchor) return;
            this.startPoint = anchor;
            this.currentPoint = anchor;
            // Write the preview immediately so the first segment is visible
            // before the next pointermove (otherwise the first wall shows no
            // preview until the mouse moves).
            this.lastPreviewKey = this.previewKey();
            this.context.appStore.setToolPreview({
                active: true,
                start: this.startPoint,
                current: this.currentPoint,
                toolId: this.id
            });
        } else {
            // Finish segment: cross-validate the end reading the same way —
            // with chain mode on, the click that starts wall #2 also commits
            // wall #1, so a ghost origin end must be resolved here too.
            const endPoint = this.resolveTrustedPoint(event, point);
            if (!endPoint) return;

            this.completeSegment(endPoint);
            // Chain mode: the next segment's start anchor is already set, so
            // write its preview immediately as well.
            if (this.startPoint) {
                this.lastPreviewKey = this.previewKey();
                this.context.appStore.setToolPreview({
                    active: true,
                    start: this.startPoint,
                    current: this.currentPoint,
                    toolId: this.id
                });
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (!point) return;

        this.currentPoint = this.resolvePoint(point);

        const nextKey = this.previewKey();
        if (nextKey === this.lastPreviewKey) {
            return;
        }
        this.lastPreviewKey = nextKey;

        this.context.appStore.setToolPreview({
            active: !!this.startPoint,
            start: this.startPoint,
            current: this.currentPoint,
            toolId: this.id
        });
    }

    onPointerUp(): void {
        // No-op for wall tool (click-click interaction)
    }

    private previewKey(): string {
        const s = this.startPoint;
        const c = this.currentPoint;
        // Ground plane is XY (Z-up world) — snapToGrid mutates x/y while z stays
        // constant, so the key must cover x/y or vertical cursor motion would be
        // treated as unchanged and the preview would freeze at a stale position.
        const sx = s ? s.x.toFixed(4) : 'n';
        const sy = s ? s.y.toFixed(4) : 'n';
        const cx = c ? c.x.toFixed(4) : 'n';
        const cy = c ? c.y.toFixed(4) : 'n';
        return `${sx},${sy}|${cx},${cy}`;
    }

    private completeSegment(endPoint: THREE.Vector3) {
        if (!this.context || !this.startPoint) return;

        if (this.startPoint.distanceTo(endPoint) < 0.1) return; // Too short

        // Create the wall
        // Get properties from store
        const toolProps = this.context.bimStore.toolProperties;

        const wall = createWall(this.startPoint, endPoint, {
            wallType: toolProps.structuralWall
                ? 'Structural Wall: Concrete - 250mm'
                : 'Basic Wall: Generic - 200mm',
            height: toolProps.wallHeight || 3000,
            baseOffset: toolProps.wallBaseOffset || 0,
            topOffset: 0,
            locationLine: toolProps.wallLocationLine || 'WallCenterline',
            chain: !!toolProps.wallChain,
            structural: !!toolProps.structuralWall,
            baseConstraint: 'Level 1',
            topConstraint: 'Unconnected'
        }, new Map([['Level 1', { elevation: 0 }], ['Level 2', { elevation: 3000 }]]));

        if (!wall) return;

        // The solver reads the join style from geometryData.joinConfiguration;
        // without it, it falls back to Butt geometry regardless of the chosen option.
        const joinType = toolProps.wallJoinType || 'Miter';
        wall.geometryData.joinConfiguration = {
            startJoinType: joinType,
            endJoinType: joinType,
            startAllowJoin: true,
            endAllowJoin: true
        };

        this.context.bimStore.addElement(wall);

        // Process Joins — in the background.
        // Joins always run — 'Butt' is a valid join style, not a signal to
        // skip joining. The solver itself decides per-end whether geometry
        // actually changes. addElement already applied the kernel's global
        // graph join pass, so the wall is visible with joined geometry right
        // away; this deferred task only refines profiles with the chosen join
        // style and records the JOIN constraints. Running it here inline used
        // to hold every click (and the command exit right after it) behind
        // the solver plus one full store refresh per neighbor.
        const bimStore = this.context.bimStore;
        scheduleBackgroundWork(() => {
            // Re-read live state: the chain may have advanced, or the wall
            // may already be gone (undo/delete) by the time the task runs.
            const liveWall = bimStore.project.elements.get(wall.id) as WallElement | undefined;
            if (!liveWall) return;

            const existingWalls = Array.from(bimStore.project.elements.values())
                .filter((e: any) => e.category === 'Walls' && e.id !== wall.id) as WallElement[];

            const joins = processWallJoins(liveWall, existingWalls, joinType);
            if (joins.length === 0) return;

            // Compute Profile for New Wall
            const connectedWalls = existingWalls.filter(w =>
                joins.some(j => j.targetId === w.id)
            );

            // One batched command = one undo entry and one store refresh for
            // the whole join settlement; per-element updateElement calls
            // would push N undo entries and trigger N full refreshes.
            const batch: Array<{ id: string; updates: Partial<BIMElement> }> = [
                {
                    id: liveWall.id,
                    updates: {
                        constraints: [...liveWall.constraints, ...joins],
                        geometryData: {
                            ...liveWall.geometryData,
                            profile: WallJoinSolver.computeWallProfile(liveWall, connectedWalls)
                        }
                    }
                },
                ...connectedWalls.map(connectedWall => ({
                    id: connectedWall.id,
                    updates: {
                        geometryData: {
                            ...connectedWall.geometryData,
                            profile: WallJoinSolver.computeWallProfile(
                                connectedWall,
                                existingWalls
                                    .filter(w => w.id !== connectedWall.id)
                                    .concat([liveWall])
                            )
                        }
                    }
                }))
            ];

            bimStore.updateElements(batch);
        });

        if (toolProps.wallChain) {
            this.startPoint = endPoint;
            this.currentPoint = endPoint;
        } else {
            this.startPoint = null;
            this.context.appStore.setToolPreview(null);
        }
        this.lastPreviewKey = null;
    }

    activate(context: ToolContext): void {
        super.activate(context);
        // Session-state reset: tools are module-level singletons, so a stale
        // anchor from a previous session must never survive re-activation
        // even if its deactivate was skipped.
        this.startPoint = null;
        this.currentPoint = null;
        this.lastPreviewKey = null;
        this.originDeferrals = 0;
    }

    deactivate() {
        // Clear the preview while the context is still available
        // (super.deactivate() nulls it).
        this.context?.appStore.setToolPreview(null);
        super.deactivate();
        this.startPoint = null;
        this.currentPoint = null;
        this.lastPreviewKey = null;
        this.originDeferrals = 0;
    }
}
